import React from 'react';
import { Alert, Button, Box, Typography } from '@mui/material';
import { Schedule as ScheduleIcon, CreditCard as CreditCardIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useAdminAccess } from '../../hooks/useAdminAccess';

/**
 * Persistent free-trial countdown banner.
 *
 * Shown to org admins while their community is on the 14-day trial (automated
 * billing). Links to /admin/billing to choose a plan. Turns to a warning tone in
 * the final stretch. (The hard paywall on expiry arrives in Phase 3.)
 */
export const TrialBanner: React.FC = () => {
  const navigate = useNavigate();
  const { config } = useTenant();
  const { isCognitoAdmin } = useAdminAccess();

  const billing = config.billing;
  // Only the org admin (who can pay) sees the trial banner.
  if (!billing || billing.state !== 'trial' || !isCognitoAdmin()) return null;

  const days = billing.daysLeft;
  const urgent = days != null && days <= 3;
  const timeLeft =
    days == null
      ? "You're on a free trial."
      : days <= 0
        ? 'Your free trial ends today.'
        : `${days} day${days === 1 ? '' : 's'} left in your free trial.`;

  return (
    <Alert
      severity={urgent ? 'warning' : 'info'}
      icon={<ScheduleIcon />}
      sx={{
        borderRadius: 0,
        borderBottom: '1px solid',
        borderBottomColor: 'divider',
        '& .MuiAlert-message': { width: '100%' },
      }}
      action={
        <Button
          color="inherit"
          size="small"
          startIcon={<CreditCardIcon />}
          onClick={() => navigate('/admin/billing')}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Choose a plan
        </Button>
      }
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Typography variant="body2">
          <strong>{timeLeft}</strong> Choose a plan to keep your community active after the trial.
        </Typography>
      </Box>
    </Alert>
  );
};

export default TrialBanner;
